import axiosInstance from "api"
import { Endpoint } from "api/enpoints.types"
import { AxiosError, AxiosResponse } from "axios"
import { useInfiniteQuery, useQuery } from "react-query"
import { IGroup, IGroupFilter } from "ts/interfaces/Group"

import {
  IInfiniteQueryBankerGroupMembersArgs,
  IInfiniteQueryGroupCandidatesArgs,
  IInfiniteQueryGroupMembersArgs,
  IPaginatedGroupMembers,
  IQueryGroupCandidatesArgs,
  IQueryGroupFilterArgs,
  IQueryGroupImportTemplateArgs,
  IQueryGroupMembersArgs,
  IQueryGroupsArgs,
} from "./groups.types"

const getGroupMembersNextPageParam = (
  lastPage: AxiosResponse<IPaginatedGroupMembers>,
  allPages: AxiosResponse<IPaginatedGroupMembers>[],
) => {
  const loadedItemCount = allPages.reduce(
    (acc, page) => acc + page.data.objects.length,
    0,
  )
  const totalItems = lastPage.data.all_objects_count
  return typeof totalItems === "number" && loadedItemCount < totalItems
    ? loadedItemCount
    : undefined
}

export const groupsQueryKey = (classId: number) => ["groups", classId]
export const groupFilterQueryKey = (classId: number) => ["groupFilter", classId]
export const bankerGroupsQueryKey = (classId: number) => [
  "bankerGroups",
  classId,
]
export const groupMembersQueryKey = (
  classId: number,
  groupId: number,
  skip?: number,
  limit?: number,
) => ["groupMembers", classId, groupId, skip, limit]
export const bankerGroupMembersQueryKey = (
  classId: number,
  groupId: number,
  skip?: number,
  limit?: number,
) => ["bankerGroupMembers", classId, groupId, skip, limit]
export const infiniteBankerGroupMembersQueryKey = (
  classId: number,
  groupId: number,
  limit?: number,
) => ["bankerGroupMembersInfinite", classId, groupId, limit]
export const groupCandidatesQueryKey = (
  classId: number,
  groupId: number,
  filter?: string,
  skip?: number,
  limit?: number,
) => ["groupCandidates", classId, groupId, filter, skip, limit]

export const useQueryGroups = (args: IQueryGroupsArgs) =>
  useQuery<AxiosResponse<IGroup[]>, AxiosError>(
    groupsQueryKey(args.classId),
    async () =>
      axiosInstance(
        Endpoint.CLASSES_ID_GROUPS.replace("CLASSID", `${args.classId}`),
      ),
    { ...args?.options },
  )

const groupFilterUrl = (classId: number) =>
  Endpoint.CLASSES_ID_GROUPS_FILTER.replace("CLASSID", `${classId}`)

export const useQueryGroupFilter = ({
  classId,
  options,
}: IQueryGroupFilterArgs) =>
  useQuery<AxiosResponse<IGroupFilter>, AxiosError>(
    groupFilterQueryKey(classId),
    async () => axiosInstance(groupFilterUrl(classId)),
    { ...options },
  )

export const useQueryBankerGroups = (args: IQueryGroupsArgs) =>
  useQuery<AxiosResponse<IGroup[]>, AxiosError>(
    bankerGroupsQueryKey(args.classId),
    async () =>
      axiosInstance(
        Endpoint.CLASSES_ID_HELPER_BANKER_GROUPS.replace(
          "ID",
          `${args.classId}`,
        ),
      ),
    { ...args?.options },
  )

const bankerGroupMembersUrl = (classId: number, groupId: number) =>
  Endpoint.CLASSES_ID_HELPER_BANKER_GROUPS_GROUPID_MEMBERS.replace(
    "ID",
    `${classId}`,
  ).replace("GROUPID", `${groupId}`)

export const useQueryBankerGroupMembers = ({
  classId,
  groupId,
  skip = 0,
  limit = 100,
  options,
}: IQueryGroupMembersArgs) =>
  useQuery<AxiosResponse<IPaginatedGroupMembers>, AxiosError>(
    bankerGroupMembersQueryKey(classId, groupId, skip, limit),
    async () =>
      axiosInstance(bankerGroupMembersUrl(classId, groupId), {
        params: { skip, limit },
      }),
    { ...options },
  )

export const useInfiniteQueryBankerGroupMembers = ({
  classId,
  groupId,
  limit = 100,
  options,
}: IInfiniteQueryBankerGroupMembersArgs) =>
  useInfiniteQuery<AxiosResponse<IPaginatedGroupMembers>, AxiosError>(
    infiniteBankerGroupMembersQueryKey(classId, groupId, limit),
    async ({ pageParam = 0 }) =>
      axiosInstance(bankerGroupMembersUrl(classId, groupId), {
        params: { skip: pageParam, limit },
      }),
    {
      ...options,
      getNextPageParam: getGroupMembersNextPageParam,
    },
  )

const groupMembersUrl = (classId: number, groupId: number) =>
  Endpoint.CLASSES_ID_GROUPS_GROUPID_MEMBERS.replace(
    "CLASSID",
    `${classId}`,
  ).replace("GROUPID", `${groupId}`)

const groupCandidatesUrl = (classId: number, groupId: number) =>
  Endpoint.CLASSES_ID_GROUPS_GROUPID_CANDIDATES.replace(
    "CLASSID",
    `${classId}`,
  ).replace("GROUPID", `${groupId}`)

export const useQueryGroupMembers = ({
  classId,
  groupId,
  skip = 0,
  limit = 100,
  options,
}: IQueryGroupMembersArgs) =>
  useQuery<AxiosResponse<IPaginatedGroupMembers>, AxiosError>(
    groupMembersQueryKey(classId, groupId, skip, limit),
    async () =>
      axiosInstance(groupMembersUrl(classId, groupId), {
        params: { skip, limit },
      }),
    { ...options },
  )

export const useInfiniteQueryGroupMembers = ({
  classId,
  groupId,
  limit = 100,
  options,
}: IInfiniteQueryGroupMembersArgs) =>
  useInfiniteQuery<AxiosResponse<IPaginatedGroupMembers>, AxiosError>(
    groupMembersQueryKey(classId, groupId),
    async ({ pageParam = 0 }) =>
      axiosInstance(groupMembersUrl(classId, groupId), {
        params: { skip: pageParam, limit },
      }),
    {
      ...options,
      getNextPageParam: getGroupMembersNextPageParam,
    },
  )

export const useQueryGroupCandidates = ({
  classId,
  groupId,
  skip = 0,
  limit = 100,
  filter,
  options,
}: IQueryGroupCandidatesArgs) =>
  useQuery<AxiosResponse<IPaginatedGroupMembers>, AxiosError>(
    groupCandidatesQueryKey(classId, groupId, filter, skip, limit),
    async () =>
      axiosInstance(groupCandidatesUrl(classId, groupId), {
        params: { skip, limit, filter: filter?.length ? filter : null },
      }),
    { ...options },
  )

export const useInfiniteQueryGroupCandidates = ({
  classId,
  groupId,
  filter,
  limit = 30,
  options,
}: IInfiniteQueryGroupCandidatesArgs) =>
  useInfiniteQuery<AxiosResponse<IPaginatedGroupMembers>, AxiosError>(
    groupCandidatesQueryKey(classId, groupId, filter),
    async ({ pageParam = 0 }) =>
      axiosInstance(groupCandidatesUrl(classId, groupId), {
        params: {
          skip: pageParam,
          limit,
          filter: filter?.length ? filter : null,
        },
      }),
    {
      ...options,
      getNextPageParam: getGroupMembersNextPageParam,
    },
  )

const groupImportTemplateUrl = (classId: number) =>
  Endpoint.CLASSES_ID_GROUPS_IMPORT_TEMPLATE.replace("CLASSID", `${classId}`)

export const useQueryGroupImportTemplate = ({
  classId,
  options,
}: IQueryGroupImportTemplateArgs) =>
  useQuery<AxiosResponse<Blob>, AxiosError>(
    ["groupImportTemplate", classId],
    async () =>
      axiosInstance(groupImportTemplateUrl(classId), { responseType: "blob" }),
    { enabled: false, ...options },
  )
